import { useCallback, useMemo } from 'react';
import { useKeepArrayIdentity } from 'src/hooks/useKeepIdentity';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';

import {
  DEFAULT_DISPLAY_METRICS,
  DisplayMetric,
  effectiveDisplayMetrics,
  isDisplayMetricArray,
  isVehicleListMetric,
  migrateLegacyDisplayMetrics,
  PersistedDisplayMetric,
} from './displayMetricKeys';

/**
 * 表示項目の localStorage キー。/map と convenience が同じ設定を共有する。
 * 変えると既存ユーザーの保存値が読めなくなるので変更しない。
 */
export const DISPLAY_METRICS_STORAGE_KEY = 'convenienceDispatch.displayMetrics';

/**
 * 表示項目の永続化 + 実効 Set の導出。キー・既定値・バリデータの 3 点セットに加え、
 * effectiveDisplayMetrics を通した Set (第3要素) までここで返す。
 *
 * 実効 Set まで hook が返す理由: 各 Presenter が effectiveDisplayMetrics(displayMetrics)
 * を自分で呼んで Set を組む形だと、呼び忘れの余地が残る。実際に Map/ConvenienceDispatches
 * の両 Presenter で呼び忘れが起き、保存値が数値指標ゼロ (粗利だけ等) のとき未割当カードの
 * 数量だけ出ない不具合が2回発生した。「hook が返したものを使うだけ」にすることで、
 * Presenter 側に導出を書く余地そのものを無くす。
 *
 * 第1要素（生の保存値）と第2要素（setter）は変更前と同じもの。DisplayMetricToggle には
 * 引き続き生の値を渡す。実効配列を渡すとトグル操作で quantity が保存値に書き込まれ、
 * 「保存値は書き換えない」という非破壊方針が壊れるため。
 *
 * useMemo で包む理由: 包まないと毎レンダーで新しい Set 参照ができ、displayMetrics が
 * 変わっていなくても memo 化された下流コンポーネント (ConvenienceDispatchVehicleList 等)
 * の再レンダー抑止が効かなくなる。
 */
export function useDisplayMetrics(): readonly [
  DisplayMetric[],
  (metrics: DisplayMetric[]) => void,
  Set<DisplayMetric>,
  Set<DisplayMetric>,
] {
  const [storedMetrics, setStoredMetrics] = usePersistedJSON<PersistedDisplayMetric[]>(
    DISPLAY_METRICS_STORAGE_KEY,
    DEFAULT_DISPLAY_METRICS,
    isDisplayMetricArray,
  );
  // 旧キーの読み替えは保存値の入口 1 か所で済ませる。各画面で読み替えると、
  // トグルの onChange が読み替え前の配列を基準に書き戻して旧キーが復活する。
  const displayMetrics = useMemo(() => migrateLegacyDisplayMetrics(storedMetrics), [storedMetrics]);
  // 書き込み側は旧キーを受け取らない（読み替え済みの値しか渡ってこない）
  const setDisplayMetrics = useCallback(
    (metrics: DisplayMetric[]) => setStoredMetrics(metrics),
    [setStoredMetrics],
  );
  const displayMetricSet = useMemo(
    () => new Set(effectiveDisplayMetrics(displayMetrics)),
    [displayMetrics],
  );

  // 左のトラックリスト向けの実効集合。中身（並びも含む）が変わらない限り参照を据え置く。
  // 実効集合をそのまま渡すと、未割当リストにしか効かない項目のトグルでも参照が変わり、
  // 全トラック行の memo と /map の buildExpandedRowVMByRowId が無駄に走る。
  // 並びまで見る useKeepArrayIdentity を使うのは、VehicleInfoView が「最後にトグルした
  // 数値指標」をゲージの対象にするため（順序を無視すると古い指標のまま据え置かれる）。
  const keepVehicleMetrics = useKeepArrayIdentity<DisplayMetric>();
  const vehicleMetricList = useMemo(
    () => keepVehicleMetrics(effectiveDisplayMetrics(displayMetrics).filter(isVehicleListMetric)),
    [displayMetrics, keepVehicleMetrics],
  );
  const vehicleDisplayMetricSet = useMemo(() => new Set(vehicleMetricList), [vehicleMetricList]);

  return [displayMetrics, setDisplayMetrics, displayMetricSet, vehicleDisplayMetricSet] as const;
}
